import React, { useEffect, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { useApp } from '../../context';

const HelpChatbot = lazy(() => import('../common/HelpChatbot'));
const PageHelpGuide = lazy(() => import('../common/PageHelpGuide'));
const OnboardingTour = lazy(() => import('../common/OnboardingTour'));

export function MainLayout() {
  const { state, dispatch } = useApp();

  // Close sidebar when resizing to mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        dispatch({ type: 'SET_SIDEBAR', payload: false });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  return (
    <div className={`flex h-screen overflow-hidden ${state.theme === 'dark' ? 'dark' : ''}`}>
      {/* Mobile overlay */}
      {state.sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar stays fixed while the page content scrolls */}
      <div className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 h-screen shrink-0
        transform transition-transform duration-300 ease-in-out
        ${state.sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <Sidebar />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col h-screen min-h-0 min-w-0 bg-surface-50 dark:bg-surface-900 transition-colors duration-200">
        <Header />
        <main className="flex-1 min-h-0 min-w-0 p-3 sm:p-4 md:p-6 overflow-y-auto overflow-x-hidden scrollbar-thin">
          <ErrorBoundary>
            <div className="page-fade-in max-w-[1600px] mx-auto">
              <Outlet />
            </div>
          </ErrorBoundary>
        </main>
      </div>
      <Suspense fallback={null}>
        <PageHelpGuide />
      </Suspense>
      <Suspense fallback={null}>
        <HelpChatbot />
      </Suspense>
      <Suspense fallback={null}>
        <OnboardingTour />
      </Suspense>
    </div>
  );
}
